<template>
  <div>
    <Card>
      <div slot="title">
        <Icon type="ios-settings"></Icon>
        设备设置
      </div>
      <div slot="extra">
        <Button @click="goBack" type="default">
          <Icon type="ios-arrow-back"></Icon>
          返回
        </Button>
      </div>
      
      <!-- 设备信息展示 -->
      <Row class="device-info" style="margin-bottom: 20px;">
        <Col span="24">
          <Alert type="info" show-icon>
            <div slot="desc">
              <p><strong>设备ID:</strong> {{ deviceInfo.deviceId }}</p>
              <p><strong>机器端口:</strong> {{ deviceInfo.macAddress }}</p>
              <p><strong>设备类型:</strong> {{ deviceInfo.type }}</p>
              <p><strong>所属工厂:</strong> {{ deviceInfo.factoryName }}</p>
            </div>
          </Alert>
        </Col>
      </Row>

      <!-- 设备状态信息 -->
      <Row style="margin-bottom: 20px;">
        <Col span="24">
          <Card title="设备状态信息">
            <div v-if="!deviceInfo.device_info" style="text-align: center; padding: 20px; color: #999;">
              暂无设备状态信息
            </div>
            <div v-else>
              <!-- 软设备状态 -->
              <div style="margin-bottom: 20px;">
                <h4>软设备状态</h4>
                <div v-if="deviceInfo.device_info.soft_device_info">
                  <Row :gutter="8">
                    <Col span="3" v-for="(device, key) in deviceInfo.device_info.soft_device_info" :key="key">
                      <div class="device-status-item" :class="{ 'camera-device': key.startsWith('camera') }">
                        <div class="device-name">
                          <Icon v-if="key.startsWith('camera')" type="ios-videocam" style="margin-right: 4px; color: #2d8cf0;"></Icon>
                          {{ device.name }}
                          <span v-if="key.startsWith('camera') && device.version" class="camera-version">v{{ device.version }}</span>
                        </div>
                        <div class="device-status" :class="device.status ? 'status-online' : 'status-offline'">
                          <Icon :type="device.status ? 'ios-checkmark-circle' : 'ios-close-circle'"></Icon>
                          {{ device.status ? '在线' : '离线' }}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
                <div v-else style="color: #999; text-align: center; padding: 20px;">
                  暂无软设备状态信息
                </div>
              </div>

              <!-- 硬件设备状态 -->
              <div>
                <h4>硬件设备状态</h4>
                <div v-if="deviceInfo.device_info.hardware_device_status_info">
                  <!-- 系统资源状态 -->
                  <Row :gutter="16" style="margin-bottom: 20px;">
                    <Col span="6">
                      <div class="hardware-status-item">
                        <div class="status-label">CPU使用率</div>
                        <div class="status-value">
                          <Progress :percent="deviceInfo.device_info.hardware_device_status_info.cpu_usage" 
                                    :stroke-color="getProgressColor(deviceInfo.device_info.hardware_device_status_info.cpu_usage)"
                                    :show-text="true"></Progress>
                        </div>
                        <div class="status-detail">温度: {{ deviceInfo.device_info.hardware_device_status_info.cpu_temperature }}°C</div>
                      </div>
                    </Col>
                    <Col span="6">
                      <div class="hardware-status-item">
                        <div class="status-label">内存使用率</div>
                        <div class="status-value">
                          <Progress :percent="deviceInfo.device_info.hardware_device_status_info.memory_usage" 
                                    :stroke-color="getProgressColor(deviceInfo.device_info.hardware_device_status_info.memory_usage)"
                                    :show-text="true"></Progress>
                        </div>
                        <div class="status-detail">{{ deviceInfo.device_info.hardware_device_status_info.memory_detail }}</div>
                      </div>
                    </Col>
                    <Col span="6">
                      <div class="hardware-status-item">
                        <div class="status-label">磁盘使用率</div>
                        <div class="status-value">
                          <Progress :percent="deviceInfo.device_info.hardware_device_status_info.disk_usage" 
                                    :stroke-color="getProgressColor(deviceInfo.device_info.hardware_device_status_info.disk_usage)"
                                    :show-text="true"></Progress>
                        </div>
                        <div class="status-detail">{{ deviceInfo.device_info.hardware_device_status_info.disk_detail }}</div>
                      </div>
                    </Col>
                    <Col span="6">
                      <div class="hardware-status-item">
                        <div class="status-label">GPU状态</div>
                        <div class="status-value">
                          <div v-if="deviceInfo.device_info.hardware_device_status_info.gpu_list && deviceInfo.device_info.hardware_device_status_info.gpu_list.length > 0">
                            <div v-for="gpu in deviceInfo.device_info.hardware_device_status_info.gpu_list" :key="gpu.index" class="gpu-item">
                              <div class="gpu-name">{{ gpu.name }}</div>
                              <div class="gpu-usage">
                                <Progress :percent="gpu.usage" 
                                          :stroke-color="getProgressColor(gpu.usage)"
                                          :show-text="true" 
                                          size="small"></Progress>
                              </div>
                              <div class="gpu-detail">
                                显存: {{ gpu.memory_used }}MB/{{ gpu.memory_total }}MB ({{ gpu.memory_percent }}%)<br>
                                温度: {{ gpu.temperature }}°C
                              </div>
                            </div>
                          </div>
                          <div v-else class="no-gpu">无GPU设备</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
                <div v-else style="color: #999; text-align: center; padding: 20px;">
                  暂无硬件设备状态信息
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <!-- 设备基础配置 -->
      <Row style="margin-bottom: 20px;">
        <Col span="24">
          <Card title="设备基础配置">
            <div style="margin-bottom: 20px;">
              <Button @click="loadDeviceConfig" type="primary" :loading="configLoading">
                <Icon type="ios-refresh"></Icon>
                刷新配置
              </Button>
              <Button @click="showConfigEditModal" type="success" style="margin-left: 10px;" :disabled="!deviceConfig">
                <Icon type="ios-settings"></Icon>
                编辑软参数配置
              </Button>
              <Button @click="showConfigParamsEditModal" type="warning" style="margin-left: 10px;" :disabled="!deviceConfig">
                <Icon type="ios-cog"></Icon>
                编辑配置参数
              </Button>
            </div>
            
            <div v-if="deviceConfig" class="config-display">
              <Alert type="info" show-icon>
                <div slot="desc">
                  <p><strong>最后更新:</strong> {{ deviceConfig.update_time || 'N/A' }}</p>
                  <p><strong>软参数配置:</strong> {{ configFormData.length }} 项 | <strong>配置参数:</strong> {{ configParamsFormData.length }} 项</p>
                </div>
              </Alert>
              
              <!-- 配置预览表格 - 水平排列 -->
              <Row :gutter="16" v-if="configFormData.length > 0 || configParamsFormData.length > 0">
                <!-- 软参数配置表格 -->
                <Col span="12" v-if="configFormData.length > 0">
                  <div class="config-preview-section">
                    <h4 style="margin-bottom: 10px; color: #2d8cf0;">
                      <Icon type="ios-settings"></Icon>
                      软参数配置
                    </h4>
                    <Table 
                      :columns="configPreviewColumns" 
                      :data="configFormData" 
                      :loading="configLoading"
                      border
                      size="small"
                      :max-height="400"
                    >
                      <template slot-scope="{ row }" slot="name">
                        <div style="font-weight: 500;">{{ row.name }}</div>
                      </template>
                      <template slot-scope="{ row }" slot="value">
                        <div style="font-weight: 500; color: #2d8cf0;">{{ row.value }}</div>
                      </template>
                      <template slot-scope="{ row }" slot="unit">
                        <div style="color: #666;">{{ row.unit || '-' }}</div>
                      </template>
                      <template slot-scope="{ row }" slot="range">
                        <div style="color: #999; font-size: 12px;">
                          <div><strong>最小值:</strong> {{ row.min }}</div>
                          <div><strong>最大值:</strong> {{ row.max }}</div>
                        </div>
                      </template>
                    </Table>
                  </div>
                </Col>
                
                <!-- 配置参数表格 -->
                <Col span="12" v-if="configParamsFormData.length > 0">
                  <div class="config-preview-section">
                    <h4 style="margin-bottom: 10px; color: #19be6b;">
                      <Icon type="ios-cog"></Icon>
                      配置参数
                    </h4>
                    <Table 
                      :columns="configParamsPreviewColumns" 
                      :data="configParamsFormData" 
                      :loading="configLoading"
                      border
                      size="small"
                      :max-height="400"
                    >
                      <template slot-scope="{ row }" slot="name">
                        <div style="font-weight: 500;">{{ row.name }}</div>
                      </template>
                      <template slot-scope="{ row }" slot="value">
                        <div style="font-weight: 500; color: #19be6b;">
                          <Tag v-if="row.type === 'bool'" :color="row.value ? 'success' : 'default'">
                            {{ row.value ? '开启' : '关闭' }}
                          </Tag>
                          <span v-else style="word-break: break-all;">{{ row.value }}</span>
                        </div>
                      </template>
                      <template slot-scope="{ row }" slot="type">
                        <Tag :color="getTypeColor(row.type)" size="small">
                          {{ getTypeName(row.type) }}
                        </Tag>
                      </template>
                      <template slot-scope="{ row }" slot="node">
                        <Tag v-if="row.node" color="blue" size="small">
                          {{ row.node }}
                        </Tag>
                        <span v-else style="color: #999;">-</span>
                      </template>
                    </Table>
                  </div>
                </Col>
              </Row>
            </div>
            <div v-else class="no-config">
              <Alert type="warning" show-icon>
                <div slot="desc">
                  暂无设备基础配置，请点击"编辑基础配置"进行设置
                </div>
              </Alert>
            </div>
          </Card>
        </Col>
      </Row>

      <!-- 设备模型配置 -->
      <Row style="margin-bottom: 20px;">
        <Col span="24">
          <Card title="设备模型配置">
            <div style="margin-bottom: 20px;">
              <Button @click="loadDeviceModels" type="primary" :loading="loading">
                <Icon type="ios-refresh"></Icon>
                刷新配置
              </Button>
            </div>
            
            <Table 
              :columns="modelColumns" 
              :data="deviceModelList" 
              :loading="loading"
              border
            >
              <template slot-scope="{ row }" slot="flawParamsJson">
                <div class="json-editor">
                  <Button 
                    type="primary" 
                    size="small"
                    @click="showJsonEditModal(row)"
                  >
                    编辑配置
                  </Button>
                </div>
              </template>
              <template slot-scope="{ row }" slot="action">
                <Button 
                  type="success" 
                  size="small" 
                  @click="showSyncModal(row)"
                >
                  <Icon type="ios-share"></Icon>
                  同步到其他设备
                </Button>
              </template>
            </Table>
          </Card>
        </Col>
      </Row>
    </Card>

    <!-- 同步配置Modal -->
    <Modal
      v-model="syncModal"
      title="同步配置到其他设备"
      :width="800"
      :mask-closable="false"
      footer-hide
    >
      <Form :model="syncForm" :label-width="120">
        <Row :gutter="16">
          <Col span="12">
            <FormItem label="源设备">
              <Input :value="`${deviceInfo.macAddress} (${deviceInfo.type})`" disabled />
            </FormItem>
          </Col>
          <Col span="12">
            <FormItem label="模型">
              <Input :value="selectedModelConfig.flawModelName" disabled />
            </FormItem>
          </Col>
        </Row>
        
        <Row>
          <Col span="24">
            <FormItem label="选择目标设备">
              <div style="margin-bottom: 10px;">
                <Checkbox 
                  :indeterminate="indeterminate"
                  :value="checkAll"
                  @click.prevent.native="handleCheckAll"
                  :disabled="syncLoading"
                >
                  全选 ({{ availableDevices.length }} 台设备)
                </Checkbox>
              </div>
              <CheckboxGroup v-model="syncForm.targetDeviceIds" @on-change="handleDeviceChange">
                <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dcdee2; padding: 8px; border-radius: 4px;">
                  <Checkbox 
                    v-for="device in availableDevices" 
                    :key="device.id" 
                    :label="device.id"
                    :disabled="syncLoading"
                    style="display: block; margin-bottom: 8px;"
                  >
                    <Tag :color="getDeviceTagColor(device.type)" size="small">
                      {{ device.type }}
                    </Tag>
                    {{ device.mac_address }} - {{ device.factorName }}
                  </Checkbox>
                </div>
              </CheckboxGroup>
            </FormItem>
          </Col>
        </Row>


        <Row>
          <Col span="24">
            <FormItem>
              <Button 
                type="primary" 
                @click="syncConfig" 
                :loading="syncLoading"
                :disabled="!syncForm.targetDeviceIds || syncForm.targetDeviceIds.length === 0"
              >
                <Icon type="ios-share"></Icon>
                确认同步
              </Button>
              <Button @click="resetSyncForm" style="margin-left: 10px;">重置</Button>
            </FormItem>
          </Col>
        </Row>
      </Form>
    </Modal>

    <!-- 设备基础配置编辑弹窗 -->
    <Modal
      v-model="configEditModal"
      title="编辑设备基础配置"
      width="1000"
      :mask-closable="false"
    >
      <div v-if="configFormData.length === 0" style="text-align: center; padding: 20px; color: #999;">
        暂无配置数据
      </div>
      <Form v-else :model="configFormData" :label-width="150" :rules="configRules" ref="configForm">
        <Row :gutter="16" v-for="(item, index) in configFormData" :key="item.code">
          <Col span="24">
            <!-- 配置名称编辑 -->
            <FormItem 
              :label="`配置名称 ${index + 1}`" 
              :prop="`${index}.name`"
              :rules="[{ required: true, message: '请输入配置名称', trigger: 'blur' }]"
            >
              <Input 
                v-model="item.name" 
                placeholder="请输入配置名称"
                style="width: 300px;"
              />
            </FormItem>
            
            <!-- 配置值编辑 -->
            <FormItem 
              :label="`配置值 ${index + 1}`" 
              :prop="`${index}.value`"
              :rules="getFieldRules(item)"
            >
              <div style="display: flex; align-items: center; gap: 10px;">
                <!-- 文本输入框 -->
                <Input 
                  v-if="item.type === 'string' || item.type === 'text'"
                  v-model="item.value" 
                  placeholder="请输入配置值"
                  style="width: 200px;"
                />
                <!-- 整数输入框 -->
                <InputNumber 
                  v-else-if="!item.float && item.type !== 'string' && item.type !== 'text'"
                  v-model="item.value" 
                  :min="item.min" 
                  :max="item.max" 
                  style="width: 200px;"
                  :precision="0"
                />
                <!-- 浮点数输入框 -->
                <InputNumber 
                  v-else
                  v-model="item.value" 
                  :min="item.min" 
                  :max="item.max" 
                  style="width: 200px;"
                  :precision="2"
                  :step="0.01"
                />
                <!-- 单位显示 -->
                <span v-if="item.unit" style="color: #666; min-width: 60px;">
                  {{ item.unit }}
                </span>
              </div>
            </FormItem>
            
            <!-- 最小值编辑 - 仅数字类型显示 -->
            <FormItem 
              v-if="item.type !== 'string' && item.type !== 'text'"
              :label="`最小值 ${index + 1}`" 
              :prop="`${index}.min`"
              :rules="[{ required: true, type: 'number', message: '请输入最小值', trigger: 'blur' }]"
            >
              <InputNumber 
                v-model="item.min" 
                style="width: 200px;"
                :precision="item.float ? 2 : 0"
                :step="item.float ? 0.01 : 1"
              />
            </FormItem>
            
            <!-- 最大值编辑 - 仅数字类型显示 -->
            <FormItem 
              v-if="item.type !== 'string' && item.type !== 'text'"
              :label="`最大值 ${index + 1}`" 
              :prop="`${index}.max`"
              :rules="getMaxFieldRules(item)"
            >
              <InputNumber 
                v-model="item.max" 
                style="width: 200px;"
                :precision="item.float ? 2 : 0"
                :step="item.float ? 0.01 : 1"
              />
            </FormItem>
            
            <!-- 其他信息显示 -->
            <div style="margin-left: 150px; margin-bottom: 16px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 12px; color: #666;">
              <div><strong>代码:</strong> {{ item.code }}</div>
              <div><strong>英文名:</strong> {{ item.en }}</div>
              <div><strong>类型:</strong> {{ getTypeName(item.type) || (item.float ? '浮点数' : '整数') }}</div>
              <div v-if="item.type !== 'string' && item.type !== 'text'">
                <strong>范围:</strong> {{ item.min }} - {{ item.max }}
              </div>
            </div>
          </Col>
        </Row>
      </Form>
      <div slot="footer">
        <Button @click="configEditModal = false">取消</Button>
        <Button type="primary" @click="saveDeviceConfig" :loading="configSaveLoading">保存</Button>
      </div>
    </Modal>

    <!-- 配置参数编辑弹窗 -->
    <Modal
      v-model="configParamsEditModal"
      title="编辑配置参数"
      width="1000"
      :mask-closable="false"
    >
      <div v-if="configParamsFormData.length === 0" style="text-align: center; padding: 20px; color: #999;">
        暂无配置参数数据
      </div>
      <Form v-else :model="configParamsFormData" :label-width="150" ref="configParamsForm">
        <Row :gutter="16" v-for="(item, index) in configParamsFormData" :key="item.code">
          <Col span="24">
            <!-- 配置名称编辑 -->
            <FormItem 
              :label="`配置名称 ${index + 1}`" 
              :prop="`${index}.name`"
              :rules="[{ required: true, message: '请输入配置名称', trigger: 'blur' }]"
            >
              <Input 
                v-model="item.name" 
                placeholder="请输入配置名称"
                style="width: 300px;"
              />
            </FormItem>
            
            <!-- 配置值编辑 -->
            <FormItem :label="`配置值 ${index + 1}`">
              <div style="display: flex; align-items: center; gap: 10px;">
                <!-- 布尔类型 -->
                <i-switch 
                  v-if="item.type === 'bool'"
                  v-model="item.value"
                />
                <!-- 开关类型 -->
                <Select 
                  v-else-if="item.type === 'switch'"
                  v-model="item.value"
                  style="width: 200px;"
                >
                  <Option 
                    v-for="option in item.data" 
                    :key="option.value" 
                    :value="option.value"
                  >
                    {{ option.name }}
                  </Option>
                </Select>
                <!-- 输入框类型 -->
                <Input 
                  v-else-if="item.type === 'input'"
                  v-model="item.value"
                  placeholder="请输入配置值"
                  style="width: 300px;"
                />
                <!-- 其他类型显示当前值（只读） -->
                <Input 
                  v-else
                  :value="item.value" 
                  disabled
                  style="width: 200px;"
                />
                <!-- 节点信息 -->
                <Tag v-if="item.node" color="blue" size="small">
                  {{ item.node }}
                </Tag>
                <!-- 英文名称 -->
                <span style="color: #666; font-size: 12px;">
                  {{ item.en }}
                </span>
              </div>
            </FormItem>
            
            <!-- 其他信息显示 -->
            <div style="margin-left: 150px; margin-bottom: 16px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 12px; color: #666;">
              <div><strong>代码:</strong> {{ item.code }}</div>
              <div><strong>英文名:</strong> {{ item.en }}</div>
              <div><strong>类型:</strong> {{ getTypeName(item.type) }}</div>
              <div v-if="item.node"><strong>节点:</strong> {{ item.node }}</div>
            </div>
          </Col>
        </Row>
      </Form>
      <div slot="footer">
        <Button @click="configParamsEditModal = false">取消</Button>
        <Button type="primary" @click="saveConfigParams" :loading="configParamsSaveLoading">保存</Button>
      </div>
    </Modal>

    <!-- JSON配置编辑弹窗 -->
    <Modal
      v-model="jsonEditModal"
      title="编辑缺陷参数配置"
      width="1200"
      :mask-closable="false"
    >
      <div v-if="currentEditRow">
        <!-- 等级切换按钮 -->
        <div style="margin-bottom: 20px; text-align: center;">
          <ButtonGroup>
            <Button 
              v-for="level in availableLevels" 
              :key="level.key"
              :type="currentLevel === level.key ? 'primary' : 'default'"
              @click="switchLevel(level.key)"
            >
              {{ level.name }}
            </Button>
          </ButtonGroup>
        </div>
        
        <div v-if="parsedJsonData.length === 0" style="text-align: center; padding: 20px; color: #999;">
          暂无数据或JSON格式错误
        </div>
        <Table 
          v-else
          :columns="jsonEditColumns" 
          :data="parsedJsonData" 
          border
          size="small"
        >
           <template slot-scope="{ row, index }" slot="cn">
             <Input 
               :value="row.cn" 
               @input="(val) => $set(parsedJsonData[index], 'cn', val)" 
               size="small"
               :style="{ color: row.real_type ? 'red' : 'inherit' }"
             />
           </template>
           <template slot-scope="{ row, index }" slot="en">
             <Input :value="row.en" @input="(val) => $set(parsedJsonData[index], 'en', val)" size="small" />
           </template>
           <template slot-scope="{ row, index }" slot="real_type">
             <span :style="{ color: row.real_type ? 'red' : '#999', fontWeight: row.real_type ? 'bold' : 'normal' }">
               {{ row.real_type ? '是' : '否' }}
             </span>
           </template>
          <template slot-scope="{ row, index }" slot="ignore">
            <i-switch :value="!row.ignore" @on-change="(val) => $set(parsedJsonData[index], 'ignore', !val)" />
          </template>
          <template slot-scope="{ row, index }" slot="index">
            <InputNumber :value="row.index" @on-change="(val) => $set(parsedJsonData[index], 'index', val)" :min="1" :max="100" size="small" />
          </template>
          <template slot-scope="{ row, index }" slot="is_mark">
            <i-switch :value="row.is_mark" @on-change="(val) => $set(parsedJsonData[index], 'is_mark', val)" />
          </template>
          <template slot-scope="{ row, index }" slot="score_pixels">
            <InputNumber :value="row.score_pixels" @on-change="(val) => $set(parsedJsonData[index], 'score_pixels', val)" :min="0.01" :max="1" :step="0.01" size="small" />
          </template>
          <template slot-scope="{ row, index }" slot="need_confirm">
            <i-switch :value="row.need_confirm" @on-change="(val) => $set(parsedJsonData[index], 'need_confirm', val)" />
          </template>
          <template slot-scope="{ row, index }" slot="auto_mark">
            <i-switch :value="row.auto_mark" @on-change="(val) => $set(parsedJsonData[index], 'auto_mark', val)" />
          </template>
          <template slot-scope="{ row, index }" slot="mini_pixels_h">
            <InputNumber :value="row.mini_pixels_h" @on-change="(val) => $set(parsedJsonData[index], 'mini_pixels_h', val)" :min="0" size="small" />
            <span style="margin-left: 5px; color: #999;">mm</span>
          </template>
          <template slot-scope="{ row, index }" slot="mini_pixels_w">
            <InputNumber :value="row.mini_pixels_w" @on-change="(val) => $set(parsedJsonData[index], 'mini_pixels_w', val)" :min="0" size="small" />
            <span style="margin-left: 5px; color: #999;">mm</span>
          </template>
        </Table>
      </div>
      <div slot="footer">
        <Button @click="jsonEditModal = false">取消</Button>
        <Button type="primary" @click="saveJsonConfig" :loading="jsonSaveLoading">保存</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { device_list, getDeviceModelList, updateDeviceModelConfig, batchSyncDeviceModel, updateDeviceFlawParams, updateDeviceFlawParamsById, getDeviceConfig, updateDeviceConfig } from "@/api/data";

export default {
  name: "device_setting",
  data() {
    return {
      deviceInfo: {
        deviceId: '',
        macAddress: '',
        type: '',
        factoryName: '',
        device_info: null
      },
      deviceModelList: [],
      availableDevices: [],
      loading: false,
      syncModal: false,
      syncLoading: false,
      selectedModelConfig: {},
      syncForm: {
        targetDeviceIds: []
      },
      checkAll: false,
      indeterminate: false,
      jsonEditModal: false,
      jsonSaveLoading: false,
      currentEditRow: null,
      parsedJsonData: [],
      // JSON编辑相关
      currentLevel: 'low', // 当前选中的等级
      availableLevels: [
        { key: 'low', name: '低等级' },
        { key: 'middle', name: '中等级' },
        { key: 'height', name: '高等级' },
        { key: 'other', name: '其他等级' }
      ],
      originalJsonData: null, // 保存原始JSON数据
      // 设备基础配置相关
      deviceConfig: null,
      configLoading: false,
      configEditModal: false,
      configSaveLoading: false,
      configFormData: [], // 动态配置表单数据
      configRules: {},
      // 配置参数相关
      configParamsEditModal: false,
      configParamsSaveLoading: false,
      configParamsFormData: [], // 配置参数表单数据
      configPreviewColumns: [
        {
          title: '配置名称',
          key: 'name',
          slot: 'name',
          width: 250,
          minWidth: 200
        },
        {
          title: '当前值',
          key: 'value',
          slot: 'value',
          width: 150,
          align: 'center'
        },
        {
          title: '单位',
          key: 'unit',
          slot: 'unit',
          width: 100,
          align: 'center'
        },
        {
          title: '取值范围',
          key: 'range',
          slot: 'range',
          width: 180,
          align: 'center'
        }
      ],
      configParamsPreviewColumns: [
        {
          title: '配置名称',
          key: 'name',
          slot: 'name',
          width: 200,
          minWidth: 150
        },
        {
          title: '当前值',
          key: 'value',
          slot: 'value',
          width: 120,
          align: 'center'
        },
        {
          title: '类型',
          key: 'type',
          slot: 'type',
          width: 80,
          align: 'center'
        },
        {
          title: '节点',
          key: 'node',
          slot: 'node',
          width: 80,
          align: 'center'
        }
      ],
      jsonEditColumns: [
        {
          title: '开关',
          key: 'ignore',
          slot: 'ignore',
          width: 80
        },
        {
          title: '疵点名称',
          key: 'cn',
          slot: 'cn',
          width: 120
        },
        {
          title: '疵点代码',
          key: 'en',
          slot: 'en',
          width: 100
        },
        {
          title: '关键疵点',
          key: 'real_type',
          slot: 'real_type',
          width: 100,
          align: 'center'
        },
        {
          title: '排序',
          key: 'index',
          slot: 'index',
          width: 80
        },
        {
          title: '置信度(>0)',
          key: 'score_pixels',
          slot: 'score_pixels',
          width: 120
        },
        {
          title: '高度(mm)',
          key: 'mini_pixels_h',
          slot: 'mini_pixels_h',
          width: 120
        },
        {
          title: '宽度(mm)',
          key: 'mini_pixels_w',
          slot: 'mini_pixels_w',
          width: 120
        },
        {
          title: '是否打标',
          key: 'is_mark',
          slot: 'is_mark',
          width: 100
        },
        {
          title: '是否停机',
          key: 'need_confirm',
          slot: 'need_confirm',
          width: 100
        },
        {
          title: '是否过滤',
          key: 'auto_mark',
          slot: 'auto_mark',
          width: 100
        }
      ],
      modelColumns: [
        {
          title: 'ID',
          key: 'id',
          width: 80
        },
        {
          title: '模型名称',
          key: 'flawModelName',
          width: 150
        },
        {
          title: '模型显示名称',
          key: 'flawModelShowName',
          width: 150
        },
        {
          title: '参数配置',
          key: 'flawParamsJson',
          slot: 'flawParamsJson',
          minWidth: 200
        },
        {
          title: '创建时间',
          key: 'createTime',
          width: 150
        },
        {
          title: '更新时间',
          key: 'updateTime',
          width: 150
        },
        {
          title: '操作',
          key: 'action',
          width: 150,
          slot: 'action'
        }
      ]
    }
  },
  mounted() {
    this.initDeviceInfo();
    this.loadDeviceConfig();
    this.loadDeviceModels();
    this.loadAvailableDevices();
  },
  watch: {
    // 监听路由变化，确保每次进入页面都重新初始化
    '$route'(to, from) {
      if (to.name === 'device_setting') {
        this.initDeviceInfo();
        this.loadDeviceConfig();
        this.loadDeviceModels();
        this.loadAvailableDevices();
        // 重置其他状态
        this.resetAllStates();
      }
    }
  },
  methods: {
    getCameraIndex(key) {
      // 提取 camera 开头键名后的编号，如 camera_0 / camera0 / camera-1 都能兼容，编号+1显示
      try {
        const match = String(key).match(/camera[_-]?(\d+)/i)
        if (match && match[1]) {
          const idx = parseInt(match[1], 10)
          return isNaN(idx) ? '' : String(idx)
        }
        return ''
      } catch (e) {
        return ''
      }
    },
    initDeviceInfo() {
      // 从路由参数获取设备信息
      const query = this.$route.query;
      this.deviceInfo = {
        deviceId: query.deviceId || '',
        macAddress: query.macAddress || '',
        type: query.type || '',
        factoryName: query.factoryName || ''
      };
    },
    async loadDeviceConfig() {
      if (!this.deviceInfo.deviceId) {
        return;
      }
      
      this.configLoading = true;
      try {
        const response = await getDeviceConfig(parseInt(this.deviceInfo.deviceId));
        
        if (response.data.code === 200) {
          this.deviceConfig = response.data.data;
          // 从接口响应中获取设备状态信息
          if (this.deviceConfig.device_info) {
            try {
              // 解析 JSON 字符串
              const deviceInfoData = JSON.parse(this.deviceConfig.device_info);
              
              // 处理 camera 数据，转换为统一格式
              if (deviceInfoData.soft_device_info) {
                const processedSoftDeviceInfo = {};
                
                Object.keys(deviceInfoData.soft_device_info).forEach(key => {
                  if (key.startsWith('camera') && !key.includes('_v_')) {
                    // 只处理原始的 camera 设备（如 camera0, camera1），跳过 camera_v_ 开头的版本字段
                    const cameraIndex = this.getCameraIndex(key);
                    const statusKey = `camera${cameraIndex}`;
                    const versionKey = `camera_v_${cameraIndex}`;
                    
                    processedSoftDeviceInfo[key] = {
                      name: `摄像头${cameraIndex}`,
                      status: deviceInfoData.soft_device_info[statusKey] || false,
                      version: deviceInfoData.soft_device_info[versionKey] || '未知版本'
                    };
                  } else if (!key.startsWith('camera_v_')) {
                    // 其他设备保持原样，但跳过 camera_v_ 开头的版本字段
                    processedSoftDeviceInfo[key] = deviceInfoData.soft_device_info[key];
                  }
                });
                
                console.log(processedSoftDeviceInfo);
                deviceInfoData.soft_device_info = processedSoftDeviceInfo;
              }
              
              this.$set(this.deviceInfo, 'device_info', deviceInfoData);
            } catch (error) {
              console.error('解析 device_info JSON 失败:', error);
              this.$set(this.deviceInfo, 'device_info', null);
            }
          }
          // 如果存在配置，解析JSON数据
          if (this.deviceConfig && this.deviceConfig.device_config_json) {
            try {
              const configData = JSON.parse(this.deviceConfig.device_config_json);
              
              // 检查新格式：包含 soft_params_json 和 config_params_json
              if (configData.soft_params_json) {
                const softParams = JSON.parse(configData.soft_params_json);
                if (Array.isArray(softParams)) {
                  this.configFormData = softParams;
                } else {
                  this.$Message.warning('软参数配置格式错误，期望数组格式');
                  this.configFormData = [];
                }
                
                // 加载配置参数
                if (configData.config_params_json) {
                  const configParams = JSON.parse(configData.config_params_json);
                  if (Array.isArray(configParams)) {
                    this.configParamsFormData = configParams;
                  } else {
                    this.configParamsFormData = [];
                  }
                } else {
                  this.configParamsFormData = [];
                }
              } else if (Array.isArray(configData)) {
                // 兼容旧格式：直接是数组
                this.configFormData = configData;
                this.configParamsFormData = [];
              } else {
                this.$Message.warning('设备配置格式错误，期望包含soft_params_json字段或数组格式');
                this.configFormData = [];
                this.configParamsFormData = [];
              }
            } catch (error) {
              console.error('解析设备配置JSON失败:', error);
              this.$Message.warning('设备配置格式错误，无法解析');
              this.configFormData = [];
            }
          } else {
            this.configFormData = [];
          }
        } else {
          this.$Message.error(response.data.message || '加载设备配置失败');
        }
      } catch (error) {
        this.$Message.error('加载设备配置失败');
        console.error(error);
      } finally {
        this.configLoading = false;
      }
    },
    showConfigEditModal() {
      this.configEditModal = true;
    },
    showConfigParamsEditModal() {
      this.configParamsEditModal = true;
    },
    async saveDeviceConfig() {
      // 表单验证
      this.$refs.configForm.validate(async (valid) => {
        if (!valid) {
          this.$Message.error('请检查表单输入');
          return;
        }

        this.configSaveLoading = true;
        try {
          // 构建新的配置格式
          let configJson;
          
          if (this.deviceConfig && this.deviceConfig.device_config_json) {
            try {
              const existingConfig = JSON.parse(this.deviceConfig.device_config_json);
              
              // 如果已有新格式，保持原有结构，只更新 soft_params_json
              if (existingConfig.soft_params_json || existingConfig.config_params_json) {
                const newConfig = {
                  ...existingConfig,
                  soft_params_json: JSON.stringify(this.configFormData)
                };
                configJson = JSON.stringify(newConfig);
              } else {
                // 旧格式，转换为新格式
                const newConfig = {
                  soft_params_json: JSON.stringify(this.configFormData),
                  config_params_json: JSON.stringify(this.configParamsFormData)
                };
                configJson = JSON.stringify(newConfig);
              }
            } catch (error) {
              // 如果解析失败，使用新格式
              const newConfig = {
                soft_params_json: JSON.stringify(this.configFormData),
                config_params_json: "[]"
              };
              configJson = JSON.stringify(newConfig);
            }
          } else {
            // 没有现有配置，使用新格式
            const newConfig = {
              soft_params_json: JSON.stringify(this.configFormData),
              config_params_json: "[]"
            };
            configJson = JSON.stringify(newConfig);
          }
          
          const response = await updateDeviceConfig(parseInt(this.deviceInfo.deviceId), configJson);
          
          if (response.data.code === 200) {
            this.$Message.success('设备配置保存成功');
            this.configEditModal = false;
            // 重新加载配置
            await this.loadDeviceConfig();
          } else {
            this.$Message.error(response.data.message || '保存失败');
          }
        } catch (error) {
          this.$Message.error('保存失败，请检查网络连接');
          console.error(error);
        } finally {
          this.configSaveLoading = false;
        }
      });
    },
    async saveConfigParams() {
      this.configParamsSaveLoading = true;
      try {
        // 构建新的配置格式
        let configJson;
        
        if (this.deviceConfig && this.deviceConfig.device_config_json) {
          try {
            const existingConfig = JSON.parse(this.deviceConfig.device_config_json);
            
            // 如果已有新格式，保持原有结构，只更新 config_params_json
            if (existingConfig.soft_params_json || existingConfig.config_params_json) {
              const newConfig = {
                ...existingConfig,
                config_params_json: JSON.stringify(this.configParamsFormData)
              };
              configJson = JSON.stringify(newConfig);
            } else {
              // 旧格式，转换为新格式
              const newConfig = {
                soft_params_json: JSON.stringify(this.configFormData),
                config_params_json: JSON.stringify(this.configParamsFormData)
              };
              configJson = JSON.stringify(newConfig);
            }
          } catch (error) {
            // 如果解析失败，使用新格式
            const newConfig = {
              soft_params_json: JSON.stringify(this.configFormData),
              config_params_json: JSON.stringify(this.configParamsFormData)
            };
            configJson = JSON.stringify(newConfig);
          }
        } else {
          // 没有现有配置，使用新格式
          const newConfig = {
            soft_params_json: JSON.stringify(this.configFormData),
            config_params_json: JSON.stringify(this.configParamsFormData)
          };
          configJson = JSON.stringify(newConfig);
        }
        
        const response = await updateDeviceConfig(parseInt(this.deviceInfo.deviceId), configJson);
        
        if (response.data.code === 200) {
          this.$Message.success('配置参数保存成功');
          this.configParamsEditModal = false;
          // 重新加载配置
          await this.loadDeviceConfig();
        } else {
          this.$Message.error(response.data.message || '保存失败');
        }
      } catch (error) {
        this.$Message.error('保存失败，请检查网络连接');
        console.error(error);
      } finally {
        this.configParamsSaveLoading = false;
      }
    },
    getFieldRules(item) {
      // 文本类型使用不同的验证规则
      if (item.type === 'string' || item.type === 'text') {
        return [
          { 
            required: true, 
            message: '请输入配置值', 
            trigger: 'blur' 
          }
        ];
      }
      
      // 数字类型使用原有的验证规则
      return [
        { 
          required: true, 
          type: 'number', 
          message: '请输入配置值', 
          trigger: 'blur' 
        },
        { 
          type: 'number', 
          min: item.min, 
          max: item.max, 
          message: `配置值必须在${item.min}到${item.max}之间`, 
          trigger: 'blur' 
        }
      ];
    },
    getMaxFieldRules(item) {
      return [
        { 
          required: true, 
          type: 'number', 
          message: '请输入最大值', 
          trigger: 'blur' 
        },
        { 
          type: 'number', 
          min: item.min, 
          message: `最大值必须大于等于最小值 ${item.min}`, 
          trigger: 'blur' 
        }
      ];
    },
    async loadDeviceModels() {
      this.loading = true;
      try {
        const response = await getDeviceModelList(1, 10, parseInt(this.deviceInfo.deviceId));
        
        if (response.data.code === 200) {
          this.deviceModelList = response.data.data.records || [];
        } else {
          this.$Message.error(response.data.msg || '加载设备模型配置失败');
        }
      } catch (error) {
        this.$Message.error('加载设备模型配置失败');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async loadAvailableDevices() {
      try {
        const params = {
          page: 1,
          pageSize: 10000
        };
        const res = await device_list(params);
        if (res.data.code === 200) {
          // 过滤掉当前设备
          this.availableDevices = (res.data.data.records || []).filter(
            device => device.id != this.deviceInfo.deviceId
          );
        }
      } catch (error) {
        this.$Message.error('加载可用设备列表失败');
        console.error(error);
      }
    },
    showSyncModal(modelConfig) {
      this.selectedModelConfig = modelConfig;
      this.syncModal = true;
    },
    async syncConfig() {
      if (!this.syncForm.targetDeviceIds || this.syncForm.targetDeviceIds.length === 0) {
        this.$Message.error('请选择要同步的目标设备');
        return;
      }

      this.$Modal.confirm({
        title: '确认同步配置',
        content: `确定要将配置同步到 ${this.syncForm.targetDeviceIds.length} 台设备吗？`,
        onOk: async () => {
          this.syncLoading = true;
          try {
            const response = await batchSyncDeviceModel(
              parseInt(this.deviceInfo.deviceId),
              this.syncForm.targetDeviceIds.join(','),
              parseInt(this.selectedModelConfig.flawModelId)
            );
            
            if (response.data.code === 200) {
              const data = response.data.data;
              
              if (data.success && data.failedCount === 0) {
                // 全部成功
                this.$Message.success(`配置同步成功！共同步 ${data.successCount} 台设备`);
                this.syncModal = false;
                this.resetSyncForm();
              } else if (data.failedCount > 0) {
                // 有失败的情况
                let message = `同步完成，成功 ${data.successCount} 台，失败 ${data.failedCount} 台设备`;
                
                if (data.failedList && data.failedList.length > 0) {
                  message += '\n失败设备：\n' + data.failedList.join('\n');
                }
                
                this.$Modal.warning({
                  title: '同步结果',
                  content: message,
                  onOk: () => {
                    this.syncModal = false;
                    this.resetSyncForm();
                  }
                });
              } else {
                this.$Message.success('配置同步成功！');
                this.syncModal = false;
                this.resetSyncForm();
              }
            } else {
              this.$Message.error(response.data.msg || '配置同步失败');
            }
          } catch (error) {
            this.$Message.error('配置同步失败：' + (error.message || '网络错误'));
            console.error(error);
          } finally {
            this.syncLoading = false;
          }
        }
      });
    },
    resetSyncForm() {
      this.syncForm.targetDeviceIds = [];
      this.selectedModelConfig = {};
      this.checkAll = false;
      this.indeterminate = false;
    },
    handleCheckAll() {
      if (this.checkAll) {
        // 取消全选
        this.syncForm.targetDeviceIds = [];
        this.checkAll = false;
        this.indeterminate = false;
      } else {
        // 全选
        this.syncForm.targetDeviceIds = this.availableDevices.map(device => device.id);
        this.checkAll = true;
        this.indeterminate = false;
      }
    },
    handleDeviceChange(selectedIds) {
      const totalCount = this.availableDevices.length;
      const selectedCount = selectedIds.length;
      
      if (selectedCount === 0) {
        this.checkAll = false;
        this.indeterminate = false;
      } else if (selectedCount === totalCount) {
        this.checkAll = true;
        this.indeterminate = false;
      } else {
        this.checkAll = false;
        this.indeterminate = true;
      }
    },
    showJsonEditModal(row) {
      this.currentEditRow = row;
      this.jsonEditModal = true;
      this.parseJsonData(row.flawParamsJson);
    },
    parseJsonData(jsonString) {
      try {
        const jsonData = JSON.parse(jsonString);
        this.originalJsonData = jsonData; // 保存原始数据
        
        // 检查是否有等级结构
        if (jsonData.low || jsonData.middle || jsonData.height || jsonData.other) {
          // 新格式：有等级结构
          this.loadLevelData(this.currentLevel);
        } else if (jsonData.bugs) {
          // 旧格式：直接有bugs
          this.loadBugsData(jsonData.bugs);
        } else {
          this.parsedJsonData = [];
        }
      } catch (error) {
        this.$Message.error('JSON格式错误');
        this.parsedJsonData = [];
      }
    },
    loadLevelData(level) {
      if (this.originalJsonData && this.originalJsonData[level] && this.originalJsonData[level].bugs) {
        this.loadBugsData(this.originalJsonData[level].bugs);
      } else {
        this.parsedJsonData = [];
      }
    },
    loadBugsData(bugs) {
      const newData = [];
      Object.keys(bugs).forEach(key => {
        const item = {
          id: key,
          cn: bugs[key].cn || '',
          en: bugs[key].en || '',
          ignore: bugs[key].ignore || false,
          index: bugs[key].index || 1,
          is_mark: bugs[key].is_mark || false,
          score_pixels: bugs[key].score_pixels || 0.01,
          need_confirm: bugs[key].need_confirm || false,
          auto_mark: bugs[key].auto_mark || false,
          mini_pixels_h: bugs[key].mini_pixels_h || 0,
          mini_pixels_w: bugs[key].mini_pixels_w || 0,
          real_type: bugs[key].real_type || false
        };
        newData.push(item);
      });
      
      // 根据 index 字段进行排序，index 越小的排在前面
      newData.sort((a, b) => (a.index || 1) - (b.index || 1));
      
      this.parsedJsonData = newData;
    },
    switchLevel(level) {
      this.currentLevel = level;
      this.loadLevelData(level);
    },
    async saveJsonConfig() {
      this.jsonSaveLoading = true;
      try {
        // 将当前编辑的数据保存到原始数据结构中
        const bugsObject = {};
        this.parsedJsonData.forEach(item => {
          const { id, ...bugData } = item;
          bugsObject[id] = bugData;
        });
        
        // 更新原始数据中对应等级的数据
        if (this.originalJsonData && (this.originalJsonData.low || this.originalJsonData.middle || this.originalJsonData.height || this.originalJsonData.other)) {
          // 新格式：有等级结构
          if (this.originalJsonData[this.currentLevel]) {
            this.originalJsonData[this.currentLevel].bugs = bugsObject;
          }
        } else {
          // 旧格式：直接有bugs
          this.originalJsonData = {
            bugs: bugsObject,
            en_name: "Knitwear20250826"
          };
        }
        
        const newJsonString = JSON.stringify(this.originalJsonData);
        
        // 调用更新接口 - 使用新的API和参数格式
        const response = await updateDeviceFlawParamsById(parseInt(this.currentEditRow.id), newJsonString);
        
        if (response.data.code === 200) {
          // 更新当前行的数据
          this.currentEditRow.flawParamsJson = newJsonString;
          this.$Message.success('配置保存成功');
          this.jsonEditModal = false;
        } else {
          this.$Message.error(response.data.msg || '保存失败');
        }
      } catch (error) {
        this.$Message.error('保存失败，请检查数据格式');
        console.error(error);
      } finally {
        this.jsonSaveLoading = false;
      }
    },
    getDeviceTagColor(type) {
      // 根据设备类型返回不同颜色
      const colors = ['blue', 'green', 'orange', 'red', 'purple'];
      const index = type.length % colors.length;
      return colors[index];
    },
    getTypeColor(type) {
      // 根据配置参数类型返回不同颜色
      const colorMap = {
        'bool': 'green',
        'switch': 'blue',
        'input': 'orange',
        'string': 'orange',
        'text': 'orange',
        'number': 'purple',
        'integer': 'purple',
        'float': 'cyan'
      };
      return colorMap[type] || 'default';
    },
    getTypeName(type) {
      // 根据配置参数类型返回中文名称
      const nameMap = {
        'bool': '布尔',
        'switch': '开关',
        'input': '输入框',
        'string': '字符串',
        'text': '文本',
        'number': '数字',
        'integer': '整数',
        'float': '浮点数'
      };
      return nameMap[type] || type;
    },
    convertDeviceType(type) {
      // 将设备类型转换，如 "kty-验布机" → "kty_model"
      if (type.includes('kty')) {
        return 'kty_model';
      }
      // 可以根据需要添加更多转换规则
      return type.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase() + '_model';
    },
    resetAllStates() {
      // 重置所有弹框状态
      this.syncModal = false;
      this.jsonEditModal = false;
      this.configEditModal = false;
      this.syncLoading = false;
      this.jsonSaveLoading = false;
      this.configSaveLoading = false;
      
      // 重置表单数据
      this.syncForm = {
        targetDeviceIds: []
      };
      this.selectedModelConfig = {};
      this.currentEditRow = null;
      this.parsedJsonData = [];
      this.currentLevel = 'low';
      this.originalJsonData = null;
      
      // 重置设备配置相关状态
      this.deviceConfig = null;
      this.configFormData = [];
      this.configParamsFormData = [];
      
      // 重置全选状态
      this.checkAll = false;
      this.indeterminate = false;
      
      // 清空数据列表
      this.deviceModelList = [];
      this.availableDevices = [];
    },
    goBack() {
      this.$router.go(-1);
    },
    // 根据使用率获取进度条颜色
    getProgressColor(percent) {
      if (percent >= 90) {
        return '#ff4d4f'; // 红色 - 危险
      } else if (percent >= 70) {
        return '#faad14'; // 橙色 - 警告
      } else if (percent >= 50) {
        return '#52c41a'; // 绿色 - 正常
      } else {
        return '#1890ff'; // 蓝色 - 良好
      }
    }
  }
}
</script>

<style scoped>
.device-info {
  margin-bottom: 20px;
}

.json-editor {
  width: 100%;
}

.json-editor .ivu-input {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.config-preview {
  background: #f5f5f5;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  padding: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.config-preview pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

.config-display {
  margin-bottom: 16px;
}

.no-config {
  margin-bottom: 16px;
}

.config-form .ivu-form-item {
  margin-bottom: 16px;
}

.config-form .ivu-form-item-label {
  font-weight: 500;
}

.config-preview-table {
  margin-top: 16px;
}

.config-preview-table .ivu-table {
  font-size: 13px;
}

.config-preview-table .ivu-table th {
  background-color: #f8f8f9;
  font-weight: 500;
}

.config-preview-table .ivu-table td {
  padding: 8px 12px;
}

.config-preview-section {
  margin-bottom: 16px;
}

.config-preview-section h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8eaec;
}

.config-preview-section .ivu-table {
  font-size: 13px;
}

.config-preview-section .ivu-table th {
  background-color: #f8f8f9;
  font-weight: 500;
}

.config-preview-section .ivu-table td {
  padding: 8px 12px;
}

/* 设备状态样式 */
.device-status-item {
  text-align: center;
  padding: 12px 8px;
  border: 1px solid #e8eaec;
  border-radius: 6px;
  background: #fafafa;
  margin-bottom: 8px;
  position: relative;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* 摄像头设备特殊样式 */
.device-status-item.camera-device {
  border-color: #2d8cf0;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
  box-shadow: 0 2px 8px rgba(45, 140, 240, 0.1);
}

.device-status-item.camera-device .device-name {
  color: #2d8cf0;
  font-weight: 600;
}

.camera-version {
  margin-left: 6px;
  color: #2d8cf0;
  font-size: 10px;
  background: rgba(45, 140, 240, 0.1);
  padding: 1px 4px;
  border-radius: 3px;
}

.device-name {
  font-size: 12px;
  font-weight: 500;
  color: #515a6e;
  margin-bottom: 6px;
  line-height: 1.3;
}

.device-status {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 4px;
}

.status-online {
  color: #19be6b;
}

.status-offline {
  color: #ed4014;
}

.device-status .ivu-icon {
  margin-right: 3px;
  font-size: 14px;
}

.device-type-tag {
  margin-top: 4px;
}

/* 硬件状态样式 */
.hardware-status-item {
  padding: 16px;
  border: 1px solid #e8eaec;
  border-radius: 6px;
  background: #fafafa;
  margin-bottom: 12px;
}

.status-label {
  font-size: 14px;
  font-weight: 500;
  color: #515a6e;
  margin-bottom: 12px;
  text-align: center;
}

.status-value {
  margin-bottom: 8px;
}

.status-detail {
  font-size: 12px;
  color: #808695;
  text-align: center;
  line-height: 1.4;
}

/* GPU状态样式 */
.gpu-item {
  margin-bottom: 12px;
  padding: 8px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e8eaec;
}

.gpu-item:last-child {
  margin-bottom: 0;
}

.gpu-name {
  font-size: 12px;
  font-weight: 500;
  color: #515a6e;
  margin-bottom: 6px;
  text-align: center;
}

.gpu-usage {
  margin-bottom: 6px;
}

.gpu-detail {
  font-size: 11px;
  color: #808695;
  text-align: center;
  line-height: 1.3;
}

.no-gpu {
  text-align: center;
  color: #c5c8ce;
  font-size: 12px;
  padding: 20px;
}
</style>
